/**
 * The combined Sent folder under Favorites: every mailbox's Sent, read live.
 *
 * The same rules as `useImapBrowse` for one folder - nothing loads on its own,
 * a stale answer is dropped, the window grows rather than pages - applied to
 * several folders at once and merged by `mergeSent`. Reading a letter opens it
 * in the folder it came from, so the pane and its downloads name the right
 * mailbox. Filing a letter is done in that mailbox's own Sent row, where the
 * right-click menu has the whole folder to act in.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getImapMessage, getImapMessages, type ImapMessageBody } from "../../../../lib/mail";
import { MAX_MESSAGES, PAGE_STEP, type ImapSource } from "./useImapBrowse";
import { mergeSent, type SentAllRow, type SentPage } from "./sentMerge";

export type { SentAllRow } from "./sentMerge";

const isAbort = (err: unknown) => err instanceof DOMException && err.name === "AbortError";

export interface SentAll {
  rows: SentAllRow[];
  total: number;
  listBusy: boolean;
  listReason: string | null;
  hasMore: boolean;
  openKey: string | null;
  open: SentAllRow | null;
  message: ImapMessageBody | null;
  msgBusy: boolean;
  msgReason: string | null;
  openRow: (row: SentAllRow) => void;
  close: () => void;
  showMore: () => void;
  reload: () => void;
}

/** `sources` null: the view is not on screen, and nothing is read. */
export function useSentAll(sources: ImapSource[] | null): SentAll {
  const [limit, setLimit] = useState(PAGE_STEP);
  const [list, setList] = useState<{ rows: SentAllRow[]; total: number; busy: boolean; reason: string | null }>({
    rows: [],
    total: 0,
    busy: false,
    reason: null,
  });
  const [picked, setPicked] = useState<{
    row: SentAllRow | null;
    message: ImapMessageBody | null;
    busy: boolean;
    reason: string | null;
  }>({ row: null, message: null, busy: false, reason: null });
  const listTicket = useRef(0);
  const msgTicket = useRef(0);
  const listCall = useRef<AbortController | null>(null);
  const msgCall = useRef<AbortController | null>(null);
  // The sources as a value, so a new array with the same folders is no reload.
  const want = sources ? sources.map((s) => `${s.mailbox}\n${s.path}`).join("\t") : null;

  const run = useCallback(
    (n: number) => {
      if (!sources) return;
      const ticket = (listTicket.current += 1);
      listCall.current?.abort();
      const call = new AbortController();
      listCall.current = call;
      setList((prev) => ({ ...prev, busy: true }));
      Promise.all(
        sources.map((s) =>
          getImapMessages(s.path, n, call.signal, s.mailbox).then(
            (res): SentPage | null =>
              res.ok
                ? { mailbox: s.mailbox, path: s.path, uidValidity: res.uidValidity, rows: res.rows, total: res.total }
                : null,
          ),
        ),
      )
        .then((pages) => {
          if (ticket !== listTicket.current) return;
          const ok = pages.filter((p): p is SentPage => p !== null);
          const merged = mergeSent(ok, n);
          // One mailbox that cannot be read is not the whole list failing; all
          // of them failing is, and then the pane says so.
          setList({ ...merged, busy: false, reason: ok.length || !pages.length ? null : "imap_error" });
        })
        .catch((err) => {
          if (ticket !== listTicket.current || isAbort(err)) return;
          setList({ rows: [], total: 0, busy: false, reason: "imap_error" });
        });
    },
    // `want` stands for `sources`: the same folders read the same way.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [want],
  );

  useEffect(() => {
    if (!want) {
      listTicket.current += 1;
      msgTicket.current += 1;
      listCall.current?.abort();
      msgCall.current?.abort();
      setList({ rows: [], total: 0, busy: false, reason: null });
      setPicked({ row: null, message: null, busy: false, reason: null });
      return;
    }
    run(limit);
  }, [want, limit, run]);

  const openRow = useCallback((row: SentAllRow) => {
    const ticket = (msgTicket.current += 1);
    msgCall.current?.abort();
    const call = new AbortController();
    msgCall.current = call;
    setPicked({ row, message: null, busy: true, reason: null });
    getImapMessage(row.path, row.uid, row.uidValidity, call.signal, row.mailbox)
      .then((res) => {
        if (ticket !== msgTicket.current) return;
        setPicked({
          row,
          message: res.ok && res.message ? res.message : null,
          busy: false,
          reason: res.ok && res.message ? null : (res.reason ?? "imap_error"),
        });
      })
      .catch((err) => {
        if (ticket !== msgTicket.current || isAbort(err)) return;
        setPicked({ row, message: null, busy: false, reason: "imap_error" });
      });
  }, []);

  const close = useCallback(() => {
    msgTicket.current += 1;
    msgCall.current?.abort();
    setPicked({ row: null, message: null, busy: false, reason: null });
  }, []);

  return {
    rows: list.rows,
    total: list.total,
    listBusy: list.busy,
    listReason: list.reason,
    hasMore: list.rows.length < list.total && limit < MAX_MESSAGES,
    openKey: picked.row?.key ?? null,
    open: picked.row,
    message: picked.message,
    msgBusy: picked.busy,
    msgReason: picked.reason,
    openRow,
    close,
    showMore: () => setLimit((n) => Math.min(MAX_MESSAGES, n + PAGE_STEP)),
    reload: () => run(limit),
  };
}
